import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LayoutDashboard, RotateCcw, Settings2, Smartphone } from "lucide-react";
import { SEASON, buildDashboard, countTools, createSetup } from "./demoData";
import { DemoSetup } from "./DemoSetup";
import { DemoDashboard } from "./DemoDashboard";
import { DemoEntry } from "./DemoEntry";
import { BTN_GHOST, EASE, GradientCheck, Kicker, Panel, Segmented } from "./ui";

const VIEWS = [
  { value: "setup", label: "Setup", icon: Settings2 },
  { value: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { value: "entry", label: "Erfassung", icon: Smartphone },
];

/** Kurze Aufbau-Sequenz: zeigt, was beim Bestätigen eines Setups passiert. */
const Building = ({ setup, onDone }) => {
  const tools = countTools(setup);
  const steps = [
    `${SEASON.homeGames} Heimspiele aus dem Spielplan übernommen`,
    tools.qr + tools.links > 0 && `${tools.qr} QR-Codes und ${tools.links} Kurzlinks erzeugt`,
    setup.blocks.social.on && "Instagram-Konto des Vereins verbunden",
    setup.blocks.website.on && "Vereinswebsite zur regelmäßigen Prüfung angemeldet",
    "Erfassungsseite und Helfer-Links für den Verein angelegt",
    "Dashboard mit Beispieldaten gefüllt",
  ].filter(Boolean);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (shown < steps.length) {
      const t = setTimeout(() => setShown((s) => s + 1), shown === 0 ? 250 : 340);
      return () => clearTimeout(t);
    }
    const t = setTimeout(onDone, 450);
    return () => clearTimeout(t);
  }, [shown, steps.length, onDone]);

  return (
    <Panel className="mx-auto max-w-xl overflow-hidden p-8 md:p-10">
      <div className="pointer-events-none absolute -top-24 left-1/4 w-72 h-72 rounded-full bg-brand-orange/15 blur-[100px]" />
      <div className="pointer-events-none absolute -bottom-24 right-1/4 w-72 h-72 rounded-full bg-brand-blue/15 blur-[100px]" />
      <div className="relative">
        <Kicker>Sponsoring wird angelegt</Kicker>
        <ul className="mt-6 space-y-4">
          {steps.map((label, i) => (
            <motion.li
              key={label}
              initial={{ opacity: 0, x: -10 }}
              animate={i < shown ? { opacity: 1, x: 0 } : { opacity: 0.25, x: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="flex items-center gap-3 text-sm text-white/80"
            >
              {i < shown ? (
                <GradientCheck />
              ) : (
                <span className="w-5 h-5 shrink-0 rounded-full border border-white/20" />
              )}
              {label}
            </motion.li>
          ))}
        </ul>
      </div>
    </Panel>
  );
};

export const DemoApp = () => {
  const [setup, setSetup] = useState(() => createSetup("baeckerei"));
  const [view, setView] = useState("setup");
  const [target, setTarget] = useState("dashboard");
  const [built, setBuilt] = useState(false);
  const [entries, setEntries] = useState([]);
  const [reports, setReports] = useState({});
  const [perspective, setPerspective] = useState("unternehmen");
  const [lastEntry, setLastEntry] = useState(null);
  const anchorRef = useRef(null);
  const rootRef = useRef(null);

  const data = useMemo(
    () => buildDashboard(setup, entries, reports, perspective),
    [setup, entries, reports, perspective]
  );

  // Beim Wechsel der Ansicht zurück an den Anfang der Demo, über Lenis.
  const scrollToDemo = () => {
    const top = rootRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) anchorRef.current?.click();
  };

  const go = (next) => {
    if (next === "setup") {
      setLastEntry(null);
      setView("setup");
    } else if (!built) {
      setTarget(next);
      setView("building");
    } else {
      if (next === "entry") setLastEntry(null);
      setView(next);
    }
    scrollToDemo();
  };

  const changeSetup = (next, { sponsorChanged } = {}) => {
    setSetup(next);
    setBuilt(false);
    if (sponsorChanged) setReports({});
  };

  // Stabile Referenz, damit die Aufbau-Sequenz ihre Zeitgeber nicht neu startet.
  const finishBuilding = useRef(() => {});
  finishBuilding.current = () => {
    setBuilt(true);
    setView(target);
  };
  const onBuilt = useCallback(() => finishBuilding.current(), []);

  const reset = () => {
    setSetup(createSetup("baeckerei"));
    setEntries([]);
    setReports({});
    setBuilt(false);
    setPerspective("unternehmen");
    setLastEntry(null);
    setView("setup");
    scrollToDemo();
  };

  const submitEntry = (entry) => {
    setEntries((prev) => [...prev.filter((e) => e.n !== entry.n), entry]);
    setLastEntry(entry);
  };

  const report = (key, amount) => setReports((prev) => ({ ...prev, [key]: (prev[key] || 0) + amount }));

  return (
    <div ref={rootRef} id="demo-app" className="scroll-mt-28">
      <a ref={anchorRef} href="#demo-app" className="sr-only" tabIndex={-1} aria-hidden="true">
        Zum Anfang der Demo
      </a>

      <div className="mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <Segmented
          layoutId="demo-view"
          label="Ansicht der Demo"
          options={VIEWS}
          value={view === "building" ? target : view}
          onChange={go}
        />
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
          <span className="inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shadow-[0_0_8px_1px_rgba(255,107,0,0.7)]" />
            Beispieldaten, nichts wird gespeichert
          </span>
          <button type="button" onClick={reset} className={BTN_GHOST}>
            <RotateCcw className="w-3.5 h-3.5" />
            Neu starten
          </button>
        </div>
      </div>

      {/* Bewusst ohne AnimatePresence: Jede Ansicht blendet beim Wechsel neu
          ein, ein Ausblenden der alten Ansicht ist nicht nötig. */}
      <motion.div
        key={view}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: EASE }}
      >
        {view === "setup" && <DemoSetup setup={setup} onChange={changeSetup} onCreate={() => go("dashboard")} />}
        {view === "building" && <Building setup={setup} onDone={onBuilt} />}
        {view === "dashboard" && (
          <DemoDashboard
            data={data}
            onPerspective={setPerspective}
            onReport={report}
            onGoto={go}
            lastEntry={lastEntry}
          />
        )}
        {view === "entry" && (
          <DemoEntry
            data={data}
            onSubmit={submitEntry}
            onGoto={go}
            onResetEntries={() => {
              setEntries([]);
              setLastEntry(null);
            }}
          />
        )}
      </motion.div>
    </div>
  );
};
